import { pageElementOf } from "./evidenceSelection"

/** Why a selection could not become a mark. Same quiet contract as
 *  `EvidencePickError` (no chip is raised), but distinct codes so tests can
 *  pin WHICH rule refused. `"too-long"` is the one exception to "quiet": the
 *  caller surfaces a short hint for it (review wave int, F1) since silently
 *  refusing a long selection with no explanation reads as a broken feature,
 *  not a rule. */
export type MarkPickError = "empty" | "cross-page" | "no-box" | "too-long"

export interface MarkBox {
  x: number
  y: number
  w: number
  h: number
}

/** A fully formed marked passage, the ask box's own list item and the shape
 *  sent to the backend as one of `marked_passages` (camelCase on the wire,
 *  see `declarationInquiriesApi.ts`). */
export interface PassageMark {
  n: number
  page: number
  quote: string
  boxes: MarkBox[]
  question: string
}

/** The capture half of a mark, before the reviewer's own `n`/`question` are
 *  attached (`StudioV2Layout` assigns `n` on insert, `AskBox` collects the
 *  question). */
export type MarkCapture = Omit<PassageMark, "n" | "question">

/** Matches `MarkedPassageIn.quote`'s backend cap (W5-1 plan). A hard ceiling
 *  now (review wave int, F1), not a truncation point: `boxes` always covers
 *  the FULL selection, so silently cutting `quote` down to this length while
 *  keeping every box left the PDF highlight and the mailed quote covering
 *  different text. Refusing the mark outright keeps the two in sync. */
const MAX_QUOTE_LENGTH = 1000

/**
 * Turn the browser's CURRENT text selection into a `{quote, page, boxes}`
 * mark: the passage picker behind "Passage markieren" (TUR-1211, W5-1).
 *
 * Unlike `captureEvidenceSelection`, a mark does NOT need to be re-findable
 * in the page's text index: it draws its own highlight from the boxes it
 * captures here, it never relies on the pdf.js text-layer matcher to relocate
 * it later. So the boxes come straight from `Range.getClientRects()`,
 * converted to fractions of the page element's own `getBoundingClientRect()`,
 * the same 0..1 page-fraction convention `EvidenceAnchorOut.bbox` already
 * uses, which is what `render_marked_pdf` multiplies by `page.rect` again on
 * the backend.
 *
 * SINGLE-PAGE BY CONTRACT, same reasoning as the evidence picker: a mark
 * carries one `page`, so a selection spanning two pages is refused, named.
 */
export function captureMarkSelection(
  root: HTMLElement,
): MarkCapture | MarkPickError {
  const selection = window.getSelection()
  if (!selection || selection.isCollapsed || selection.rangeCount === 0) {
    return "empty"
  }
  // L6 (live test, wave 5): a stored quote started two characters into the
  // word ("CTITE 406" instead of "LOCTITE 406"), its box starting at the
  // same wrong character. The old code read the quote off
  // `selection.toString()` and the box geometry off `selection.getRangeAt(0)`
  // directly: two representations of "the same" capture that are not the
  // same object, can disagree (a pdf.js text layer's `Selection.toString()`
  // is already known to diverge from its raw text, see
  // `evidenceSelection.ts`'s docstring), and `.trim()` below only ever
  // shortened the STRING, never the RANGE the boxes came from. One Range is
  // now the single source for both: `quote` and `boxes` cannot disagree.
  const rawRange = selection.getRangeAt(0)
  const quote = rawRange.toString().trim()
  if (!quote) return "empty"
  // Checked on the trimmed string BEFORE any box geometry runs (review wave
  // int, F1): a selection over the backend's cap must produce no mark at
  // all, not a truncated quote next to boxes still covering the untrimmed
  // selection (see MAX_QUOTE_LENGTH's own doc above).
  if (quote.length > MAX_QUOTE_LENGTH) return "too-long"

  const startPage = pageElementOf(selection.anchorNode)
  const endPage = pageElementOf(selection.focusNode)
  if (!startPage || !root.contains(startPage)) return "cross-page"
  if (endPage !== startPage) return "cross-page"

  const page = Number(startPage.getAttribute("data-page-number"))
  if (!Number.isFinite(page) || page < 1) return "cross-page"

  const pageRect = startPage.getBoundingClientRect()
  const range = trimRange(startPage, rawRange)
  const boxes: MarkBox[] = Array.from(range.getClientRects())
    .filter((r) => r.width > 0 && r.height > 0)
    .map((r) => ({
      x: clampFraction((r.left - pageRect.left) / pageRect.width),
      y: clampFraction((r.top - pageRect.top) / pageRect.height),
      w: clampFraction(r.width / pageRect.width),
      h: clampFraction(r.height / pageRect.height),
    }))
  if (boxes.length === 0) return "no-box"

  return {
    quote,
    page,
    boxes,
  }
}

function clampFraction(value: number): number {
  const clamped = Math.min(1, Math.max(0, value))
  return Math.round(clamped * 10000) / 10000
}

/**
 * Shrink `range` to the bounds of its own trimmed text, so the geometry
 * `getClientRects()` produces below matches the `quote` string exactly
 * character-for-character (see the L6 note above `captureMarkSelection`).
 *
 * A pdf.js text layer can render a run's leading/trailing space as its own
 * span, so "skip N whitespace characters" may need to cross into an
 * adjacent text node, not just move the offset within the current one:
 * `advanceStart`/`retreatEnd` walk `root`'s text nodes in document order to
 * do that. Bails out (returns the original range) when there is nothing to
 * trim, the common case, so no extra clone/walk runs per selection.
 */
function trimRange(root: HTMLElement, range: Range): Range {
  const text = range.toString()
  const leading = text.length - text.trimStart().length
  const trailing = text.length - text.trimEnd().length
  if (leading === 0 && trailing === 0) return range

  const trimmed = range.cloneRange()
  if (leading > 0) advanceStart(root, trimmed, leading)
  if (trailing > 0) retreatEnd(root, trimmed, trailing)
  return trimmed
}

/** Moves `range`'s start forward by `chars` characters, in document order,
 *  crossing into the next text node under `root` when the current one runs
 *  out. */
function advanceStart(root: HTMLElement, range: Range, chars: number): void {
  let node = range.startContainer
  let offset = range.startOffset
  let remaining = chars
  while (remaining > 0 && node.nodeType === Node.TEXT_NODE) {
    const available = (node as Text).data.length - offset
    if (remaining <= available) {
      offset += remaining
      break
    }
    remaining -= available
    const next = adjacentTextNode(root, node, "next")
    if (!next) {
      offset = (node as Text).data.length
      break
    }
    node = next
    offset = 0
  }
  range.setStart(node, offset)
}

/** Moves `range`'s end backward by `chars` characters, in document order,
 *  crossing into the previous text node under `root` when the current one
 *  runs out. */
function retreatEnd(root: HTMLElement, range: Range, chars: number): void {
  let node = range.endContainer
  let offset = range.endOffset
  let remaining = chars
  while (remaining > 0 && node.nodeType === Node.TEXT_NODE) {
    if (remaining <= offset) {
      offset -= remaining
      break
    }
    remaining -= offset
    const prev = adjacentTextNode(root, node, "previous")
    if (!prev) {
      offset = 0
      break
    }
    node = prev
    offset = (node as Text).data.length
  }
  range.setEnd(node, offset)
}

/** The next/previous text node under `root`, in document order, after/before
 *  `node` (itself a text node inside `root`). */
function adjacentTextNode(
  root: HTMLElement,
  node: Node,
  direction: "next" | "previous",
): Text | null {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
  walker.currentNode = node
  const found = direction === "next" ? walker.nextNode() : walker.previousNode()
  return (found as Text | null) ?? null
}
